/**
 * Config Bus — Schema registry (Sprint 16, epic REFACTOR-030).
 *
 * Single source of verità per le chiavi di configurazione GeoFleet: nome, valore
 * di default, scope ('user' | 'company') e tipo. Sostituisce i ~30 fallback inline
 * sparsi nei consumer (es. `config?.x || default`), che INVESTIGATION-002 ha
 * rilevato come incoerenti e non centralizzati.
 *
 * NB (Sprint 18): i default delle chiavi del **focus Settings/consumer** sono stati
 * RICONCILIATI coi fallback inline reali dei consumer (e i relativi reader migrati a
 * `value(key)` adottano questi default). Restano `verify:true` solo le chiavi NON ancora
 * adottate da un reader via schema: `activeMap` (GeofleetMap, fuori focus), `chart` (default
 * multi-sorgente, `useAnalyzePageConfig` fa il proprio merge → non centralizzato), `sort`/
 * `activeFilters` (MainPanel ha hydration custom, default non load-bearing), `poiSettings`
 * (company, Sprint 19). Confermarli prima di adottarne lo schema in un reader.
 *
 * Vedi `docs/architecture/config-bus.md`.
 */

import cloneDeep from 'lodash/cloneDeep'

// Gancio per future migration dello schema. Nessuna migration necessaria ora: il
// blob salvato è un oggetto piatto e le chiavi mancanti ricadono sui default.
export const CONFIG_VERSION = 1

/**
 * Registro delle chiavi. Ogni voce: { default, scope, type, verify? }.
 * `type` è descrittivo (documentazione/validazione futura), non impone coercizione.
 */
export const CONFIG_SCHEMA = {
    // ─── Vehicle list / identificazione ───────────────────────────────────
    vehicleListModeView: { default: 'list', scope: 'user', type: 'string' },
    vehicleListFluidTable: { default: false, scope: 'user', type: 'boolean' },
    vehicleIdentification: { default: null, scope: 'user', type: 'string' },
    copyIdentificationToMarker: { default: false, scope: 'user', type: 'boolean' },
    // Sprint 18: default riconciliato col consumer reale (GeoFleetSettings.resetData).
    truckListItemFields: {
        default: ['address', 'speed', 'fuel_battery_level', 'altitude'],
        scope: 'user',
        type: 'array'
    },
    // NON è un array ma un oggetto `{ fields: [...] }`, dove ogni voce è una chiave del
    // catalogo colonne (composables/useTruckColumns.js) — `normalizeTableFields()` valida il
    // salvato contro quel catalogo e tollera il vecchio formato `{ value, label }`.
    vehicleListTableFields: {
        default: { fields: ['status', 'plate', 'activity', 'speed', 'address', 'actions'] },
        scope: 'user',
        type: 'object'
    },

    // ─── Icona marker (tipo/colore) ───────────────────────────────────────
    iconType: { default: null, scope: 'user', type: 'object' },
    copyIconTypeToMarker: { default: false, scope: 'user', type: 'boolean' },
    iconColor: { default: null, scope: 'user', type: 'object' },
    copyIconColorToMarker: { default: false, scope: 'user', type: 'boolean' },

    // ─── Mappa / cluster / marker ─────────────────────────────────────────
    activeVehicleClustering: { default: true, scope: 'user', type: 'boolean' },
    vehicleMarkerSize: { default: [52, 66], scope: 'user', type: 'array' },
    vehicleMarkerFontSize: { default: '10px', scope: 'user', type: 'string' },
    vehicleMarkerFontColor: { default: '#000000', scope: 'user', type: 'string' },
    vehicleMarkerBackgroundColor: { default: '#ffffff', scope: 'user', type: 'string' },
    vehicleMarkerOpacity: { default: 50, scope: 'user', type: 'number' },
    vehicleMarkerAdditionalData: { default: [], scope: 'user', type: 'array' },
    vehicleAdditionalInfoBackgroundColor: { default: '#ffffff', scope: 'user', type: 'string' },
    vehicleAdditionalInfoFontColor: { default: '#000000', scope: 'user', type: 'string' },
    vehicleAdditionalInfoOpacity: { default: 50, scope: 'user', type: 'number' },
    // Sprint 19: default riconciliato col default della prop `map` di GeofleetMap ('osm'):
    // i reader che la passano come prop (ActivityPanel/ActivityWrapper/Planner) ottenevano
    // 'osm' via prop-default quando la chiave era assente (undefined). ⚠️ NON adottare
    // `value('activeMap')` nel watch interno di GeofleetMap: lì l'assenza NON deve
    // sovrascrivere la prop (GeoFleet passa `map="silica"`) → present-check su sotto-chiave.
    activeMap: { default: 'osm', scope: 'user', type: 'string' },
    infoLayers: { default: [], scope: 'user', type: 'array' },
    // Sprint 18: stato dei layer POI sulla mappa (array di `{ id, selected, label }`). Assente
    // → null (NON []): il watch `'config.poisForm'` di GeofleetMap fa `if (poisForm)`, un []
    // sovrascriverebbe il form derivato dai POI. Writer già migrato a commitChange in Sprint 17.
    poisForm: { default: null, scope: 'user', type: 'array' },

    // ─── Indirizzi / autisti ──────────────────────────────────────────────
    // Sprint 18: default riconciliati coi consumer reali (GeoFleetSettings / DriversStatusSettings).
    addressComposition: {
        default: ['road', 'postcode', 'city', 'country'],
        scope: 'user',
        type: 'array'
    },
    driversStatus: { default: { colors: {} }, scope: 'user', type: 'object' },
    driversStatusColors: { default: {}, scope: 'user', type: 'object' },

    // ─── Grafico attività (ActivityPanel) ─────────────────────────────────
    // Struttura annidata: speed/activity/altitude/consumption. Default conservativo;
    // confermare contro AnalyzePageConfig al momento della migrazione.
    chart: {
        default: {
            speed: { active: true },
            activity: { active: true, top: '0%', height: '100%', opposite: false },
            altitude: { active: false },
            consumption: { active: false, format: '' }
        },
        scope: 'user',
        type: 'object',
        verify: true
    },
    // Telemetrie mostrate nei popup/tooltip della mappa del route analyzer. Stesso
    // catalogo di `chart` ma selezione INDIPENDENTE (tab "Map settings"). Default {}:
    // l'assenza deve far ricadere il lettore su `chart` (continuità per gli utenti che
    // non hanno mai aperto il tab) — il merge vero è in `useAnalyzePageConfig`.
    mapTelemetries: { default: {}, scope: 'user', type: 'object', verify: true },
    timeline_break: { default: 15, scope: 'user', type: 'number' },
    timeline_separator: { default: 0, scope: 'user', type: 'number' },

    // ─── Pannello dettaglio / main panel ──────────────────────────────────
    detail_page: { default: null, scope: 'user', type: 'string' },
    pinned_trucks: { default: [], scope: 'user', type: 'array' },
    sort: { default: {}, scope: 'user', type: 'object', verify: true },
    activeFilters: { default: [], scope: 'user', type: 'array', verify: true },

    // ─── Planner / servizi esterni ────────────────────────────────────────
    loggedToAlpega: { default: false, scope: 'user', type: 'boolean' },
    // Sprint 19: filtri/ultima ricerca della borsa carichi (writer in store/freight-exchange,
    // migrati da `saveConfig` a `commitChange`). Assenti → null (NON {}): i consumer fanno
    // presence-check (`if (config.freightExchangeFilters)` in getFEFilters decide se fare
    // fallback su sessionStorage/model) — un {} truthy salterebbe quei rami.
    freightExchangeFilters: { default: null, scope: 'user', type: 'object' },
    freightExchangeLastSearch: { default: null, scope: 'user', type: 'object' },

    // ─── Company scope ────────────────────────────────────────────────────
    // Impostazioni a livello azienda (salvate con type='company' sul backend).
    poiSettings: { default: {}, scope: 'company', type: 'object', verify: true }
}

/** Tutte le chiavi note. */
export function configKeys() {
    return Object.keys(CONFIG_SCHEMA)
}

/** True se `key` è una chiave nota dello schema. */
export function isKnownKey(key) {
    return Object.prototype.hasOwnProperty.call(CONFIG_SCHEMA, key)
}

/** Scope di una chiave ('user' | 'company'); 'user' come fallback per chiavi ignote. */
export function scopeOf(key) {
    return CONFIG_SCHEMA[key]?.scope || 'user'
}

/**
 * Default di una chiave. Ritorna una COPIA per i tipi reference (array/object) così
 * che lo state non condivida riferimenti con lo schema (evita mutazioni accidentali).
 */
export function defaultOf(key) {
    const def = CONFIG_SCHEMA[key]?.default
    return cloneDefault(def)
}

/** Oggetto di default per uno scope ('user' | 'company'). */
export function defaultsFor(scope) {
    const out = {}
    for (const key of Object.keys(CONFIG_SCHEMA)) {
        if (CONFIG_SCHEMA[key].scope === scope) {
            out[key] = cloneDefault(CONFIG_SCHEMA[key].default)
        }
    }
    return out
}

function cloneDefault(value) {
    // Deep-clone dei tipi reference (array/oggetti, anche annidati) così lo state
    // non condivide riferimenti con lo schema; i primitivi passano diretti.
    if (value && typeof value === 'object') return cloneDeep(value)
    return value
}
